import React from 'react'
import { Typography } from '@material-ui/core'
import { CoreButton } from 'src/components/core'
import { Icon } from '@iconify/react'
import { withStyles } from '@material-ui/core'
import styles from './styles'
import { messageBoxShowAction } from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { useDispatch } from 'react-redux'
import CollectionEdit from 'src/components/collection-edit'
import useTrackerFunction from 'src/hooks/useTrackerFunction'

const FEATURE_SECTIONS = [
  {
    title: '🔮 Landing pages',
    description:
      'With richer templates and and a drag-and-drop editor, customize a polished first impression that speaks to your learners. Add a custom domain to boost your credibility.',
  },
  {
    title: '🎓 Learners',
    description:
      'Keep your collection open to all or make it exclusive to your custom list of learners with member-only access. Use built-in analytics to monitor engagement.',
  },
  {
    title: '🏆 Certificates',
    description:
      'Celebrate achievements with built-in certificates. Recognize progress, validate educational milestones, and let learners share their success—inspiring future learners.',
  },
  {
    title: '💰 Monetization',
    description:
      'With paywall integrations through Stripe, Lemon Squeezy, and Pabbly, manage who accesses your content while turning your expertise into income.',
  },
  {
    title: '📱 In-App learning',
    description:
      'Teach without switching platforms. Deliver training directly inside your product—no need to redirect users elsewhere. Keep learning right where it matters.',
  },
]

const CollectionEmptyState = ({ classes }) => {
  const dispatch = useDispatch()

  // Tracking functions
  const trackCreateCollection = useTrackerFunction(
    'clicked_create_collection',
    {
      source: 'collection_empty_state',
      location: 'top',
    },
  )
  const trackCreateFirstCollection = useTrackerFunction(
    'clicked_create_collection',
    {
      source: 'collection_empty_state',
      location: 'bottom',
    },
  )

  const handleOpenCollectionEdit = location => {
    // Track based on location
    if (location === 'top') {
      trackCreateCollection()
    } else {
      trackCreateFirstCollection()
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'New collection',
          draggable: false,
          maxWidth: '500px',
          component: <CollectionEdit />,
        }),
      ),
    )
  }

  return (
    <>
      <div className={classes.fullWidthDiv}>
        <div className={classes.content}>
          <Typography fontSize="20px" fontWeight={600}>
            Discover Collections: <i>Your</i> Learning Hub
          </Typography>
          <Typography fontSize="16px" fontWeight={400} color="#637381">
            A collection is a curated group of mini courses around a specific
            topic or theme, allowing you to structure your own learning hub in a
            way that works best for your learners.
          </Typography>
          <CoreButton
            variant="contained"
            color="primary"
            startIcon={<Icon icon="mdi:plus" />}
            onClick={() => handleOpenCollectionEdit('top')}
          >
            Create Collection
          </CoreButton>
          <Typography fontSize="12px" fontWeight={400} color="#637381">
            Want to dive in and create your collection right away?
          </Typography>
        </div>
        <img
          src={`/assets/collection-landing/coll-${1}.png`}
          alt="page-image"
          height="360px"
          width="510px"
        />
      </div>

      <div className={classes.fullWidthDivWithTwoItem}>
        {FEATURE_SECTIONS.slice(0, 2).map((section, index) => (
          <div key={index} className={classes.independentContent}>
            <Typography fontSize="20px" fontWeight={600}>
              {section.title}
            </Typography>
            <Typography fontSize="16px" fontWeight={400} color="#637381">
              {section.description}
            </Typography>
            <img
              src={`/assets/collection-landing/coll-${index + 2}.svg`}
              alt="page-image"
            />
          </div>
        ))}
      </div>

      <div className={classes.fullWidthDivWithThreeItem}>
        {FEATURE_SECTIONS.slice(2).map((section, index) => (
          <div key={index} className={classes.independentContentThreeItem}>
            <Typography fontSize="20px" fontWeight={600}>
              {section.title}
            </Typography>
            <Typography fontSize="16px" fontWeight={400} color="#637381">
              {section.description}
            </Typography>
            <img
              src={`/assets/collection-landing/coll-${index + 4}.svg`}
              alt="page-image"
            />
          </div>
        ))}
      </div>
      <div className={classes.fullWidthDivMid}>
        <div className={classes.text}>
          <Typography
            fontSize="28px"
            fontWeight={500}
            className={classes.gradientText}
          >
            Create. Curate. Empower.
          </Typography>
          <Typography fontSize="28px" fontWeight={500}>
            Your collection starts now.
          </Typography>
        </div>
        <CoreButton
          variant="contained"
          color="primary"
          startIcon={<Icon icon="mdi:plus" />}
          onClick={() => handleOpenCollectionEdit('bottom')}
        >
          Create your first collection
        </CoreButton>
      </div>
    </>
  )
}

export default withStyles(styles)(CollectionEmptyState)
